import type { ReactElement } from 'react';
import { useRef } from 'react';
import type { LivingBriefDetail, ProposalBuildPage, ProposalBuildPageStatus, ReviewItem } from '@shared/api';
import type { Proposal } from '@shared/types';
import { BuildPageCard } from './PageCard';
import { openReviewItemsForPage } from '../../lib/reviewItems';

/**
 * Pages that newly left `planned` in this render pass get a stagger delay
 * (85ms apart, in page order); everything else keeps whatever it already
 * settled into. The ref makes this a one-time entrance per page id — a
 * later re-render (new snapshot, SSE reconnect) never replays it.
 */
function useRevealDelays(pages: ProposalBuildPage[]): Map<string, number> {
  const revealedRef = useRef<Set<string>>(new Set());
  const prevStatusRef = useRef<Map<string, ProposalBuildPageStatus>>(new Map());

  const delays = new Map<string, number>();
  const newlyRevealed = pages
    .filter((page) => {
      if (page.status === 'planned') return false;
      if (revealedRef.current.has(page.id)) return false;
      const previous = prevStatusRef.current.get(page.id) ?? 'planned';
      return previous === 'planned';
    })
    .slice()
    .sort((a, b) => a.order - b.order);

  newlyRevealed.forEach((page, index) => {
    delays.set(page.id, index * 85);
    revealedRef.current.add(page.id);
  });
  pages.forEach((page) => prevStatusRef.current.set(page.id, page.status));

  return delays;
}

export function PageGrid({
  pages,
  proposal,
  onAttentionClick,
  onRetryPage,
  details = [],
  reviewItems = [],
}: {
  pages: ProposalBuildPage[];
  proposal: Proposal;
  onAttentionClick?: (page: ProposalBuildPage) => void;
  onRetryPage?: (page: ProposalBuildPage) => void;
  details?: LivingBriefDetail[];
  reviewItems?: ReviewItem[];
}): ReactElement {
  const revealDelays = useRevealDelays(pages);
  const orderedPages = pages.slice().sort((a, b) => a.order - b.order);

  return (
    <div className="page-grid-region">
      <div className="page-grid" role="list" aria-label="Proposal pages">
        {orderedPages.map((page) => (
          <BuildPageCard
            key={page.id}
            page={page}
            proposal={proposal}
            revealDelayMs={revealDelays.get(page.id)}
            onAttentionClick={onAttentionClick}
            onRetry={onRetryPage}
            reviewCount={openReviewItemsForPage(reviewItems, page.id).length}
            placeholder={details.find((detail) => detail.status === 'missing' && detail.affectedPageIds.includes(page.id))?.placeholder}
          />
        ))}
      </div>
    </div>
  );
}
